/**
 * Task Service
 * Handles task CRUD operations and business logic
 * Pure data operations - UI updates handled by caller
 */

import { looksLikeDMY, looksLikeISO, toISOFromDMY } from "../utils/date.js";

/**
 * Keep completedDate in step with a status transition: stamp it on every move
 * into done and clear it when the task leaves done, so a re-completed task is
 * dated to its latest completion. Mutates and returns the task.
 * @param {Object} task - Task whose status has already been set
 * @param {string|undefined} previousStatus - Status before the change (undefined for new tasks)
 * @returns {Object} The same task
 */
export function applyCompletedDateTransition(task, previousStatus) {
    if (!task || task.status === previousStatus) return task;
    if (task.status === 'done') {
        task.completedDate = new Date().toISOString();
    } else if (task.completedDate) {
        task.completedDate = null;
    }
    return task;
}

const LEGACY_COMPLETION_OFFSET_DAYS = 30;

/**
 * Best-guess completion day for a done task saved before completedDate was
 * tracked: its end date, else its creation date + 1 month. A guess that lands
 * after today falls back to the creation day, so no legacy task is counted as
 * completed recently.
 * @param {Object} task - Done task with no completedDate
 * @param {string} todayIso - Today as YYYY-MM-DD
 * @returns {string|null} ISO timestamp (noon UTC of the chosen day) or null when there is no usable date
 */
export function inferLegacyCompletedDate(task, todayIso) {
    const isoDay = (v) => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) ? v.slice(0, 10) : '';
    const createdDay = isoDay(task.createdAt) || isoDay(task.createdDate);

    let day = isoDay(task.endDate);
    if (!day && createdDay) {
        const createdMs = Date.parse(`${createdDay}T00:00:00Z`);
        if (Number.isFinite(createdMs)) {
            day = new Date(createdMs + LEGACY_COMPLETION_OFFSET_DAYS * 86400000).toISOString().slice(0, 10);
        }
    }
    if (day && day > todayIso) day = createdDay && createdDay <= todayIso ? createdDay : '';
    return day ? `${day}T12:00:00.000Z` : null;
}

/**
 * One-time backfill: give every done task that lacks a completedDate the date
 * from inferLegacyCompletedDate. Templates are skipped. Mutates the tasks.
 * @param {Array} tasks - All tasks
 * @param {string} todayIso - Today as YYYY-MM-DD
 * @returns {Array<{id: number, completedDate: string}>} The tasks that changed
 */
export function backfillLegacyCompletedDates(tasks, todayIso) {
    const changed = [];
    for (const task of tasks || []) {
        if (!task || task.isTemplate || task.status !== 'done' || task.completedDate) continue;
        const completedDate = inferLegacyCompletedDate(task, todayIso);
        if (!completedDate) continue;
        task.completedDate = completedDate;
        changed.push({ id: task.id, completedDate });
    }
    return changed;
}

/**
 * Create a new task
 * @param {Object} taskData - Task data from form
 * @param {Array} tasks - Current tasks array
 * @param {number} taskCounter - Current task counter
 * @param {Array} tempAttachments - Temporary attachments array
 * @returns {{task: Object, tasks: Array, taskCounter: number}} Created task and updated arrays
 */
export function createTask(taskData, tasks, taskCounter, tempAttachments = []) {
    const now = new Date().toISOString();
    const startDate = taskData.startDate || "";
    const endDate = taskData.endDate || "";
    const newTask = {
        id: taskCounter,
        title: taskData.title || "",
        description: taskData.description || "",
        projectId: taskData.projectId ? parseInt(taskData.projectId, 10) : null,
        startDate,
        endDate,
        startDateWasEverSet: typeof startDate === 'string' && startDate.trim() !== "",
        endDateWasEverSet: typeof endDate === 'string' && endDate.trim() !== "",
        priority: taskData.priority || "medium",
        status: taskData.status || "todo",
        tags: taskData.tags || [],
        attachments: tempAttachments.length > 0 ? [...tempAttachments] : [],
        automationId: taskData.automationId != null ? parseInt(taskData.automationId, 10) : null,
        createdAt: now,
        updatedAt: now,
    };
    applyCompletedDateTransition(newTask, undefined);

    const updatedTasks = [...tasks, newTask];
    const updatedCounter = taskCounter + 1;

    return {
        task: newTask,
        tasks: updatedTasks,
        taskCounter: updatedCounter
    };
}

/**
 * Update an existing task
 * @param {number} taskId - ID of task to update
 * @param {Object} taskData - Updated task data
 * @param {Array} tasks - Current tasks array
 * @returns {{task: Object|null, tasks: Array, oldProjectId: number|null}} Updated task and arrays
 */
export function updateTask(taskId, taskData, tasks) {
    const taskIndex = tasks.findIndex(t => t.id === parseInt(taskId, 10));

    if (taskIndex === -1) {
        return { task: null, tasks, oldProjectId: null };
    }

    const oldTask = tasks[taskIndex];
    const oldProjectId = oldTask.projectId;

    const nextProjectId = taskData.projectId !== undefined
        ? (taskData.projectId ? parseInt(taskData.projectId, 10) : null)
        : oldTask.projectId;

    const updatedTask = {
        ...oldTask,
        title: taskData.title !== undefined ? taskData.title : oldTask.title,
        description: taskData.description !== undefined ? taskData.description : oldTask.description,
        projectId: nextProjectId,
        startDate: taskData.startDate !== undefined ? taskData.startDate : oldTask.startDate,
        endDate: taskData.endDate !== undefined ? taskData.endDate : oldTask.endDate,
        priority: taskData.priority !== undefined ? taskData.priority : oldTask.priority,
        status: taskData.status !== undefined ? taskData.status : oldTask.status,
    };

    if (taskData.startDate !== undefined && typeof updatedTask.startDate === 'string' && updatedTask.startDate.trim() !== "") {
        updatedTask.startDateWasEverSet = true;
    }
    if (taskData.endDate !== undefined && typeof updatedTask.endDate === 'string' && updatedTask.endDate.trim() !== "") {
        updatedTask.endDateWasEverSet = true;
    }

    const changed =
        updatedTask.title !== oldTask.title ||
        updatedTask.description !== oldTask.description ||
        updatedTask.projectId !== oldTask.projectId ||
        updatedTask.startDate !== oldTask.startDate ||
        updatedTask.endDate !== oldTask.endDate ||
        updatedTask.priority !== oldTask.priority ||
        updatedTask.status !== oldTask.status;

    if (!changed) {
        return {
            task: oldTask,
            tasks,
            oldProjectId
        };
    }

    applyCompletedDateTransition(updatedTask, oldTask.status);
    updatedTask.updatedAt = new Date().toISOString();

    const updatedTasks = [...tasks];
    updatedTasks[taskIndex] = updatedTask;

    return {
        task: updatedTask,
        tasks: updatedTasks,
        oldProjectId
    };
}

/**
 * Update a single field of a task
 * @param {number} taskId - ID of task to update
 * @param {string} field - Field name to update
 * @param {any} value - New value for the field
 * @param {Array} tasks - Current tasks array
 * @returns {{task: Object|null, tasks: Array, oldProjectId: number|null}} Updated task and arrays
 */
export function updateTaskField(taskId, field, value, tasks, settings = {
    autoSetStartDateOnStatusChange: false,
    autoSetEndDateOnStatusChange: false
}) {
    const taskIndex = tasks.findIndex(t => t.id === parseInt(taskId, 10));

    if (taskIndex === -1) {
        return { task: null, tasks, oldProjectId: null };
    }

    const oldTask = tasks[taskIndex];
    const oldProjectId = oldTask.projectId;
    const updatedTask = { ...oldTask };

    const prevStartDate = oldTask.startDate;
    const prevEndDate = oldTask.endDate;
    const prevCompletedDate = oldTask.completedDate;

    // Handle date fields with conversion
    if (field === 'startDate' || field === 'endDate') {
        const iso = looksLikeDMY(value) ? toISOFromDMY(value)
            : looksLikeISO(value) ? value
            : "";
        updatedTask[field] = iso;
        if (iso) {
            if (field === 'startDate') updatedTask.startDateWasEverSet = true;
            if (field === 'endDate') updatedTask.endDateWasEverSet = true;
        }
    } else if (field === 'projectId') {
        updatedTask.projectId = value ? parseInt(value, 10) : null;
    } else {
        updatedTask[field] = value;
    }

    // Auto-set dates when status changes (if setting is enabled)
    if (field === 'status' && (settings.autoSetStartDateOnStatusChange || settings.autoSetEndDateOnStatusChange)) {
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

        // Auto-set startDate when moving to "In Progress" (if empty)
        if (settings.autoSetStartDateOnStatusChange && value === 'progress' && !updatedTask.startDate) {
            updatedTask.startDate = today;
            updatedTask.startDateWasEverSet = true;
        }

        // Auto-set endDate when moving to "Done" (if empty)
        if (settings.autoSetEndDateOnStatusChange && value === 'done' && !updatedTask.endDate) {
            updatedTask.endDate = today;
            updatedTask.endDateWasEverSet = true;
        }
    }

    if (field === 'status') {
        applyCompletedDateTransition(updatedTask, oldTask.status);
    }

    let changed;
    if (field === 'projectId') {
        changed = updatedTask.projectId !== oldTask.projectId;
    } else {
        changed = updatedTask[field] !== oldTask[field];
    }

    // Status changes can also auto-set date fields / completedDate
    if (field === 'status') {
        if (updatedTask.startDate !== prevStartDate) changed = true;
        if (updatedTask.endDate !== prevEndDate) changed = true;
        if (updatedTask.completedDate !== prevCompletedDate) changed = true;
    }

    if (!changed) {
        return {
            task: oldTask,
            tasks,
            oldProjectId
        };
    }

    updatedTask.updatedAt = new Date().toISOString();

    const updatedTasks = [...tasks];
    updatedTasks[taskIndex] = updatedTask;

    return {
        task: updatedTask,
        tasks: updatedTasks,
        oldProjectId
    };
}

/**
 * Delete a task
 * @param {number} taskId - ID of task to delete
 * @param {Array} tasks - Current tasks array
 * @returns {{task: Object|null, tasks: Array, projectId: number|null}} Deleted task info and updated array
 */
export function deleteTask(taskId, tasks) {
    const task = tasks.find(t => t.id === parseInt(taskId, 10));

    if (!task) {
        return { task: null, tasks, projectId: null };
    }

    const projectId = task.projectId;
    const updatedTasks = tasks.filter(t => t.id !== parseInt(taskId, 10));

    return {
        task,
        tasks: updatedTasks,
        projectId
    };
}

/**
 * Duplicate a task
 * @param {number} taskId - ID of task to duplicate
 * @param {Array} tasks - Current tasks array
 * @param {number} taskCounter - Current task counter
 * @returns {{task: Object|null, tasks: Array, taskCounter: number}} Duplicated task and updated arrays
 */
export function duplicateTask(taskId, tasks, taskCounter) {
    const original = tasks.find(t => t.id === parseInt(taskId, 10));

    if (!original) {
        return { task: null, tasks, taskCounter };
    }

    // Always add "Copy " prefix (Copy Test -> Copy Copy Test, etc.)
    const baseTitle = original.title || "Untitled";
    const newTitle = `Copy ${baseTitle}`;

    const now = new Date().toISOString();
    const cloned = {
        id: taskCounter,
        title: newTitle,
        description: original.description || "",
        projectId: original.projectId ?? null,
        startDate: original.startDate || "",
        endDate: original.endDate || "",
        startDateWasEverSet: !!original.startDateWasEverSet || (!!original.startDate && String(original.startDate).trim() !== ""),
        endDateWasEverSet: !!original.endDateWasEverSet || (!!original.endDate && String(original.endDate).trim() !== ""),
        priority: original.priority || "medium",
        status: original.status || "todo",
        tags: Array.isArray(original.tags) ? [...original.tags] : [],
        attachments: Array.isArray(original.attachments)
            ? original.attachments.map(a => ({...a}))
            : [],
        automationId: null,
        createdAt: now,
        updatedAt: now,
    };

    const updatedTasks = [...tasks, cloned];
    const updatedCounter = taskCounter + 1;

    return {
        task: cloned,
        tasks: updatedTasks,
        taskCounter: updatedCounter
    };
}

/**
 * Validate task data
 * @param {Object} taskData - Task data to validate
 * @returns {{valid: boolean, errors: Array<string>}} Validation result
 */
export function validateTask(taskData) {
    const errors = [];

    if (!taskData.title || taskData.title.trim() === "") {
        errors.push("Title is required");
    }

    if (taskData.priority && !['low', 'medium', 'high'].includes(taskData.priority)) {
        errors.push("Invalid priority value");
    }

    if (taskData.status && !['todo', 'progress', 'review', 'done'].includes(taskData.status)) {
        errors.push("Invalid status value");
    }

    return {
        valid: errors.length === 0,
        errors
    };
}
